// src/components/common/SectionHeader.js

import React, { useState } from 'react';
import './SectionHeader.css';

/**
 * Unified section header with optional share button
 *
 * @param {string} title - The section title (displayed uppercase)
 * @param {string} sectionName - Used for share URL and tracking (e.g., 'big-story')
 * @param {string} content - Raw content for generating tweet excerpt
 * @param {boolean} shareable - Whether to show share button (default: true)
 */
const SectionHeader = ({
  title,
  sectionName,
  content = '',
  shareable = true
}) => {
  const [showShareSuccess, setShowShareSuccess] = useState(false);

  /**
   * Generate a tweet-length excerpt from content
   * Target: ~180 chars to leave room for URL (~23 chars) and breathing room
   */
  const generateTweetExcerpt = (rawContent) => {
    if (!rawContent) return '';

    // Clean the content
    let cleaned = rawContent
      .replace(/\[[^\]]+\]/g, '')           // Remove citation markers [1], [2: url], etc.
      .replace(/\*\*([^*]+)\*\*/g, '$1')    // Remove bold markers
      .replace(/\*([^*]+)\*/g, '$1')        // Remove italic markers
      .replace(/^#{1,6}\s+/gm, '')          // Remove heading markers
      .replace(/\n+/g, ' ')                 // Replace newlines with spaces
      .replace(/\s+/g, ' ')                 // Collapse multiple spaces
      .trim();

    // Extract first sentence or truncate at ~180 chars
    const firstSentence = cleaned.match(/^[^.!?]+[.!?]/);
    if (firstSentence && firstSentence[0].length <= 180) {
      return firstSentence[0];
    }

    // Truncate at word boundary around 180 chars
    if (cleaned.length > 180) {
      const truncated = cleaned.substring(0, 180);
      const lastSpace = truncated.lastIndexOf(' ');
      return truncated.substring(0, lastSpace) + '...';
    }

    return cleaned;
  };

  const shareToTwitter = () => {
    const excerpt = generateTweetExcerpt(content);
    const shareUrl = `https://skimz.ai/s/${sectionName}.html`;

    const tweetText = excerpt || `${title} — via Skimz`;

    const twitterUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(tweetText)}`;

    window.open(twitterUrl, '_blank', 'noopener,noreferrer');

    setShowShareSuccess(true);
    setTimeout(() => setShowShareSuccess(false), 2500);

    if (window.mixpanel) {
      window.mixpanel.track('Section Share', {
        section: sectionName,
        platform: 'twitter'
      });
    }
  };

  return (
    <div className="section-header">
      <h2 className="section-header__title">
        {title}
      </h2>

      {shareable && (
        <button
          className="section-header__share-btn"
          onClick={shareToTwitter}
          aria-label={`Share ${title} on X`}
          title="Share on X"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
          </svg>
        </button>
      )}

      {showShareSuccess && (
        <span className="section-header__toast" role="status">
          Opening X...
        </span>
      )}
    </div>
  );
};

export default SectionHeader;
